import { useState } from "react";
import {
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Table,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  PasswordInput,
  Switch,
  MultiSelect,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { usersApi } from "../../api/users.api";
import { rolesApi } from "../../api/roles.api";
import { apiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: usersApi.list,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: rolesApi.list,
  });

  const removeMut = useMutation({
    mutationFn: usersApi.remove,
    onSuccess: () => {
      notifications.show({ color: "green", message: "User deleted" });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Users</Title>
          <Text c="dimmed">Manage middleware accounts</Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          New User
        </Button>
      </Group>

      <Paper p={0}>
        {isLoading ? (
          <Group p="lg">
            <Loader size="sm" /> <Text c="dimmed">Loading…</Text>
          </Group>
        ) : (
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last login</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {u.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>{u.fullName}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {u.roles.map((r) => (
                        <Badge key={r.id} variant="light">
                          {r.name}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {u.isActive ? (
                      <Badge color="green">Active</Badge>
                    ) : (
                      <Badge color="gray">Disabled</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {u.lastLoginAt
                        ? new Date(u.lastLoginAt).toLocaleString()
                        : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => setEditing(u)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={u.id === currentUser?.id}
                        onClick={() => {
                          if (
                            confirm(`Delete user ${u.email}? This cannot be undone.`)
                          ) {
                            removeMut.mutate(u.id);
                          }
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <UserFormModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        roleOptions={roleOptions}
      />
      <UserFormModal
        opened={!!editing}
        user={editing}
        onClose={() => setEditing(null)}
        roleOptions={roleOptions}
      />
    </Stack>
  );
}

function UserFormModal({ opened, onClose, user, roleOptions }) {
  const qc = useQueryClient();
  const isEdit = !!user;

  const form = useForm({
    initialValues: {
      email: user?.email || "",
      fullName: user?.fullName || "",
      password: "",
      isActive: user?.isActive ?? true,
      roleIds: user?.roles?.map((r) => r.id) || [],
    },
    validate: {
      email: (v) =>
        /^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required",
      fullName: (v) => (v.length > 0 ? null : "Required"),
      password: (v) =>
        isEdit
          ? v.length === 0 || v.length >= 6
            ? null
            : "Minimum 6 characters"
          : v.length >= 6
          ? null
          : "Minimum 6 characters",
    },
  });

  const mut = useMutation({
    mutationFn: (values) => {
      if (isEdit) {
        const payload = { ...values };
        if (!payload.password) delete payload.password;
        return usersApi.update(user.id, payload);
      }
      return usersApi.create(values);
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: isEdit ? "User updated" : "User created",
      });
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
      form.reset();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? `Edit user · ${user.email}` : "Create user"}
      size="md"
    >
      <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
        <Stack>
          <TextInput
            label="Email"
            required
            {...form.getInputProps("email")}
          />
          <TextInput
            label="Full name"
            required
            {...form.getInputProps("fullName")}
          />
          <PasswordInput
            label={isEdit ? "New password (leave blank to keep current)" : "Password"}
            required={!isEdit}
            {...form.getInputProps("password")}
          />
          <MultiSelect
            label="Roles"
            data={roleOptions}
            searchable
            {...form.getInputProps("roleIds")}
          />
          <Switch
            label="Active"
            {...form.getInputProps("isActive", { type: "checkbox" })}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mut.isPending}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
